import { FC } from 'react';
import { Text, View } from '@react-pdf/renderer';

import Table from '../PdfTable';
import styles from './styles';
import { numberToCurrencyString } from '../../utils';

interface Props {
  taxYear: string;
  taxReliefs: {
    items: Array<{
      name: string;
      investmentDate: string;
      investmentType: string;
      uir: string;
      formattedCapitalAllocated: string;
      formattedTaxRelief: string;
      isKiFundTaxRelief: boolean;
    }>;
    eisSubtotal: {
      capitalAllocated: string;
      taxRelief: string;
    };
    seisSubtotal: {
      capitalAllocated: string;
      taxRelief: string;
    };
  };
  totals: {
    capitalAllocated: string;
    taxRelief: string;
  };
  kiFundsInvestmentsValue: number;
}

const TaxReliefs: FC<Props> = ({
  taxYear,
  taxReliefs,
  totals,
  kiFundsInvestmentsValue,
}) => {
  if (!taxReliefs.items.length) {
    return (
      <View wrap={false}>
        <Text style={styles.heading}>Income tax relief</Text>
        <Text style={styles.paragraph}>
          You do not have any income tax relief available to claim for the{' '}
          {taxYear} tax year.
        </Text>
      </View>
    );
  }

  return (
    <>
      <View wrap={false}>
        <Text style={styles.heading}>Income tax relief</Text>
        <Text style={styles.paragraph}>
          You may claim UK income tax relief on the following investments made
          this tax year:
        </Text>
      </View>
      <Table
        title={null}
        backgroundColor="white"
        fontColor="#333333"
        alignContent="left"
        fontSize={8}
        fullBorder
        expandRows
        columns={{
          investment: { value: 'Investment', bold: true },
          investmentDate: {
            value: 'Investment date',
            bold: true,
            width: 16,
          },
          reliefType: { value: 'Relief type', bold: true, width: 12 },
          uir: { value: 'UIR', bold: true, width: 14 },
          valueOfShares: {
            value: 'Value of shares',
            bold: true,
            width: 18,
          },
          taxRelief: {
            value: 'Claimable tax relief',
            bold: true,
            width: 18,
          },
        }}
        data={[
          ...taxReliefs.items.map((taxRelief) => ({
            investment: {
              value: taxRelief.name,
            },
            investmentDate: {
              value: taxRelief.investmentDate,
              width: 16,
            },
            reliefType: {
              value: taxRelief.investmentType,
              width: 12,
            },
            uir: { value: taxRelief.uir, width: 14 },
            valueOfShares: {
              value: taxRelief.formattedCapitalAllocated,
              width: 18,
            },
            taxRelief: {
              value: taxRelief.formattedTaxRelief,
              width: 18,
            },
          })),
          // Add EIS subtotal row
          {
            investment: { value: '' },
            investmentDate: { value: '', width: 16 },
            reliefType: { value: '', width: 12 },
            uir: { value: 'Subtotal: EIS', bold: true, width: 14 },
            valueOfShares: {
              value: taxReliefs.eisSubtotal.capitalAllocated,
              bold: true,
              width: 18,
            },
            taxRelief: {
              value: taxReliefs.eisSubtotal.taxRelief,
              bold: true,
              width: 18,
            },
          },
          // Add SEIS subtotal row
          {
            investment: { value: '' },
            investmentDate: { value: '', width: 16 },
            reliefType: { value: '', width: 12 },
            uir: { value: 'Subtotal: SEIS', bold: true, width: 14 },
            valueOfShares: {
              value: taxReliefs.seisSubtotal.capitalAllocated,
              bold: true,
              width: 18,
            },
            taxRelief: {
              value: taxReliefs.seisSubtotal.taxRelief,
              bold: true,
              width: 18,
            },
          },
        ]}
        footer={{
          investment: {
            value: '',
          },
          investmentDate: {
            value: '',
            width: 16,
          },
          reliefType: {
            value: '',
            width: 12,
          },
          uir: { value: 'Total', bold: true, width: 14 },
          valueOfShares: {
            value: totals.capitalAllocated,
            bold: true,
            width: 18,
          },
          taxRelief: {
            value: totals.taxRelief,
            bold: true,
            width: 18,
          },
        }}
      />
      <Text style={[styles.paragraph, styles.topMargin]}>
        All S/EIS certificates relating to the above investments are attached to
        this letter. Any investments with 'Pending' displayed above are yet to
        receive S/EIS certificates or confirmation of S/EIS status.
      </Text>
      <Text style={styles.paragraph}>
        If you have a 'Pending' investment, you should re-download this report
        from Further once your applicable fund manager has processed S/EIS
        information for your applicable investment(s). The Further system will
        update any newly downloaded report automatically.
      </Text>
      {kiFundsInvestmentsValue > 0 && (
        <>
          <Text style={styles.paragraphItalic}>
            Investments in approved KI EIS Funds
          </Text>
          <Text style={styles.paragraph}>
            You invested {numberToCurrencyString(kiFundsInvestmentsValue)} into
            knowledge-intensive EIS funds during this tax year. Tax relief for
            each knowledge-intensive fund investment is claimable through an
            EIS5 certificate. Your applicable fund manager(s) will update you
            directly when an EIS5 certificate becomes available. At that point,
            this report will be updated to include any EIS5 relating to this tax
            year. Please note that EIS5 certificates can be issued up to two
            years following each fund subscription, depending on that fund's
            deployment progress.
          </Text>
        </>
      )}
    </>
  );
};

export default TaxReliefs;
